import {FeatureFlag} from '../../../../@type/features';
import {type IAmazonPaySession, type IAmazonSessionPayload} from '../../models/IAmazon';
import {Carts, DefaultCart} from '../../reducers/cartReducer';
import {Environment, Feature} from '../../reducers/environmentReducer';
import {MerchantConfiguration} from '../../reducers/merchantConfigurationReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {getBaseURL} from '../../util/environment';
import {amazonCustomerAddress} from './utils';

/**
 * Used to generate the amazon pay signature required for rendering an amazon pay buttons
 * @param payload
 * @param sandbox
 * @returns Signature string
 */
async function generateAmazonPaySignature(payload: IAmazonSessionPayload, sandbox: boolean): Promise<string> {
	const response = await fetch(
		getBaseURL(MerchantConfiguration.hostName(), Environment.testMode())
			+ 'api/v1/amazonpay/signature',
		{
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify({
				session: {
					id: PeachPayOrder.sessionId(),
					merchant_id: MerchantConfiguration.id(),
					merchant_url: MerchantConfiguration.hostName(),
					merchant_name: MerchantConfiguration.name(),
					plugin_version: Environment.plugin.version(),
					platform: 'woocommerce',
				},
				transaction: {
					amazonpay: {
						payload,
						sandbox,
					},
				},
			}),
		},
	);

	if (!response.ok) {
		return '';
	}

	const result = await response.json() as {signature: string};
	return result.signature;
}

/**
 * Builds a data object containing all related values for an amazon session.
 * @param returnURL URL the checkout session will return to when completed
 * @returns
 */
async function AmazonPaySession(returnURL: string): Promise<IAmazonPaySession> {
	const publicKeyId = Feature.metadata<string>(FeatureFlag.AMAZON_GATEWAY, 'public_key_id') ?? '';
	const storeId = Feature.metadata<string>(FeatureFlag.AMAZON_GATEWAY, 'store_id') ?? '';
	const merchantId = Feature.metadata<string>(FeatureFlag.AMAZON_GATEWAY, 'merchant_id') ?? '';
	const sandbox = Environment.isTestOrDevSite();
	const toFixed = MerchantConfiguration.currency.configuration().number_of_decimals;
	const addressDetails = Carts.virtual() ? {} : {addressDetails: amazonCustomerAddress()};

	const payload: IAmazonSessionPayload = {
		webCheckoutDetails: {
			checkoutResultReturnUrl: returnURL,
			checkoutMode: 'ProcessOrder',
		},
		storeId,
		scopes: ['name', 'email', 'phoneNumber', 'billingAddress'],
		paymentDetails: {
			paymentIntent: 'AuthorizeWithCapture',
			chargeAmount: {
				amount: (DefaultCart.total()).toFixed(toFixed),
				currencyCode: MerchantConfiguration.currency.code(),
			},
			presentmentCurrency: MerchantConfiguration.currency.code(),
		},
		...addressDetails,
	};
	const signature = await generateAmazonPaySignature(payload, sandbox);

	return {
		publicKeyId,
		storeId,
		merchantId,
		sandbox,
		addressDetails,
		payload,
		signature,
	};
}

export {AmazonPaySession};
